// Phase 18 · Community · Feed page (root + category)
import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { Plus, LogIn } from "lucide-react";
import PostCard from "../../components/community/PostCard";
import CategorySidebar from "../../components/community/CategorySidebar";
import TrendingWidget from "../../components/community/TrendingWidget";
import UpsellBanner from "@/components/upsell/UpsellBanner";
import CreatePostModal from "./CreatePostModal";
import { useAuth } from "../../context/AuthContext";
// F13.1 Wave C · marketing hero above the live feed.
import {
    ProductHero, PrimaryBtn, SecondaryBtn,
} from "@/components/marketing/MarketingPrimitives";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommunityFeed() {
    const { category } = useParams();
    const location = useLocation();
    const search = new URLSearchParams(location.search);
    const sort = search.get("sort") || "recent";
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setDisabled(false);
        try {
            const params = new URLSearchParams();
            if (category) params.set("category", category);
            params.set("sort", sort);
            params.set("limit", "20");
            const r = await fetch(`${API}/api/community/feed?${params}`, { credentials: "include" });
            if (r.status === 503) {
                setDisabled(true);
                setItems([]);
                setTotal(0);
                return;
            }
            if (!r.ok) throw new Error("feed failed");
            const d = await r.json();
            setItems(d.items || []);
            setTotal(d.total || 0);
        } catch { setItems([]); }
        finally { setLoading(false); }
    }, [category, sort]);

    useEffect(() => { load(); }, [load]);

    return (
        <div
            className="min-h-screen bg-[#0A0A0A] text-[#FFFFFF] font-inter"
            data-testid="community-feed-page"
        >
            {/* F13.1 Wave C · marketing hero above the live feed */}
            <ProductHero
                eyebrow="COMMUNITY"
                title="Join the GlobalLeadSuite community"
                subtitle="Connect with operators, agencies, and builders scaling autonomous lead generation."
                status="early"
                primaryCTA={<PrimaryBtn to="/register" testid="community-marketing-cta-register">Join free</PrimaryBtn>}
                secondaryCTA={
                    <SecondaryBtn href="#" testid="community-marketing-cta-social">Discord · LinkedIn · X</SecondaryBtn>
                }
                testid="community-marketing-hero"
            />
            <header className="sticky top-0 z-40 backdrop-blur-md bg-[#0A0A0A]/80 border-b border-[#1A1A1A]">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link
                            to="/"
                            className="text-lg font-bold bg-white bg-clip-text text-transparent"
                        >
                            Global Lead Suite
                        </Link>
                        <span className="text-neutral-600">·</span>
                        <h1 className="text-base font-semibold text-neutral-200">
                            Community
                            {category && (
                                <span className="text-neutral-500 ml-2 font-normal">
                                    / {category}
                                </span>
                            )}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {user ? (
                            <button
                                type="button"
                                onClick={() => setCreating(true)}
                                className="inline-flex items-center gap-2 bg-white text-neutral-900 font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition-all text-sm"
                                data-testid="community-create-post-btn"
                            >
                                <Plus size={16} />
                                New post
                            </button>
                        ) : (
                            <Link
                                to="/"
                                className="inline-flex items-center gap-2 border border-[#FFFFFF]/40 text-[#FFFFFF] hover:bg-[#FFFFFF]/10 px-4 py-2 rounded-lg font-semibold text-sm transition-all"
                                data-testid="community-login-cta"
                            >
                                <LogIn size={16} />
                                Sign in
                            </Link>
                        )}
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 py-6">
                <UpsellBanner surface="community_feed" />
                {disabled && (
                    <div className="bg-[#FAFAFA]/10 border border-[#FAFAFA]/30 rounded-lg p-4 text-sm text-[#FAFAFA] mb-6">
                        Community is currently disabled by admin (kill switch). Check back soon.
                    </div>
                )}
                <div className="flex flex-col lg:flex-row gap-6">
                    <CategorySidebar activeSlug={category} />
                    <div className="flex-1 min-w-0" data-testid="community-feed-list">
                        <div className="mb-4 flex items-center gap-3 text-xs">
                            <Link
                                to={category ? `/community/${category}` : "/community"}
                                className={`px-3 py-1.5 rounded-full border transition-all ${sort === "recent" ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                                data-testid="sort-recent"
                            >
                                Recent
                            </Link>
                            <Link
                                to={category ? `/community/${category}?sort=top` : "/community?sort=top"}
                                className={`px-3 py-1.5 rounded-full border transition-all ${sort === "top" ? "bg-[#FFFFFF]/10 border-[#FFFFFF]/40 text-[#FFFFFF]" : "border-[#1A1A1A] text-neutral-400 hover:border-[#FFFFFF]/30"}`}
                                data-testid="sort-top"
                            >
                                Top
                            </Link>
                            <span className="text-neutral-500 ml-auto">
                                {total} posts
                            </span>
                        </div>
                        {loading && (
                            <div className="space-y-3">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="h-32 bg-[#111111] border border-[#1A1A1A] rounded-xl animate-pulse" />
                                ))}
                            </div>
                        )}
                        {!loading && !items.length && !disabled && (
                            <div className="text-center py-12 bg-[#111111] border border-[#1A1A1A] rounded-xl">
                                <p className="text-neutral-400">No posts yet</p>
                                <p className="text-xs text-neutral-500 mt-1">
                                    {user ? "Be the first to create one." : "Sign in to create the first post."}
                                </p>
                            </div>
                        )}
                        <div className="space-y-3">
                            {items.map((p) => (
                                <PostCard key={p.id} post={p} />
                            ))}
                        </div>
                    </div>
                    <TrendingWidget />
                </div>
            </main>

            {creating && (
                <CreatePostModal
                    onClose={() => setCreating(false)}
                    onCreated={(newId) => {
                        setCreating(false);
                        load();
                    }}
                    initialCategory={category}
                />
            )}
        </div>
    );
}
